import { useEffect, useRef } from 'react';

export function CommunityBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let time = 0;

    interface NetworkNode {
      x: number;
      y: number;
      vx: number;
      vy: number;
      size: number;
      color: string;
      isAvatar: boolean;
      initials?: string;
      pulsePhase: number;
    }

    interface Heart {
      x: number;
      y: number;
      size: number;
      opacity: number;
      vy: number;
      phase: number;
    }

    let nodes: NetworkNode[] = [];
    let hearts: Heart[] = [];

    const colors = ['#a855f7', '#22d3ee', '#3b82f6', '#10b981', '#f59e0b', '#ec4899'];
    const initials = ['BK', 'GY', 'EE', 'AS', 'MD', 'AY', 'ZK', 'OT'];

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initElements();
    };

    const initElements = () => {
      nodes = [];
      hearts = [];

      // Create network nodes (some as avatars)
      const nodeCount = Math.floor((canvas.width * canvas.height) / 40000);
      for (let i = 0; i < nodeCount; i++) {
        const isAvatar = i < 8;
        nodes.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 0.3,
          vy: (Math.random() - 0.5) * 0.3,
          size: isAvatar ? 25 : 4 + Math.random() * 4,
          color: colors[Math.floor(Math.random() * colors.length)],
          isAvatar,
          initials: isAvatar ? initials[i % initials.length] : undefined,
          pulsePhase: Math.random() * Math.PI * 2,
        });
      }

      // Create floating hearts
      for (let i = 0; i < 5; i++) {
        hearts.push({
          x: Math.random() * canvas.width,
          y: canvas.height + Math.random() * 200,
          size: 15 + Math.random() * 10,
          opacity: 0.1 + Math.random() * 0.1,
          vy: -0.3 - Math.random() * 0.3,
          phase: Math.random() * Math.PI * 2,
        });
      }
    };

    const drawConnections = () => {
      ctx.lineWidth = 1;

      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          const maxDistance = nodes[i].isAvatar || nodes[j].isAvatar ? 200 : 100;

          if (distance < maxDistance) {
            const opacity = (1 - distance / maxDistance) * 0.15;
            ctx.strokeStyle = `rgba(168, 85, 247, ${opacity})`;
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();

            // Animated pulse along connection
            if (nodes[i].isAvatar || nodes[j].isAvatar) {
              const pulsePos = (time * 0.5 + i * 0.1) % 1;
              const pulseX = nodes[i].x + (nodes[j].x - nodes[i].x) * pulsePos;
              const pulseY = nodes[i].y + (nodes[j].y - nodes[i].y) * pulsePos;

              ctx.beginPath();
              ctx.arc(pulseX, pulseY, 3, 0, Math.PI * 2);
              ctx.fillStyle = `rgba(168, 85, 247, ${opacity * 2})`;
              ctx.fill();
            }
          }
        }
      }
    };

    const drawNodes = () => {
      nodes.forEach((node) => {
        node.x += node.vx;
        node.y += node.vy;

        // Bounce off edges
        if (node.x < 0 || node.x > canvas.width) node.vx *= -1;
        if (node.y < 0 || node.y > canvas.height) node.vy *= -1;

        const pulse = Math.sin(time * 2 + node.pulsePhase) * 0.2 + 1;

        if (node.isAvatar) {
          // Avatar glow
          const gradient = ctx.createRadialGradient(
            node.x, node.y, 0,
            node.x, node.y, node.size * 2
          );
          gradient.addColorStop(0, node.color.replace(')', ', 0.2)').replace('rgb', 'rgba').replace('#a855f7', 'rgba(168, 85, 247').replace('#22d3ee', 'rgba(34, 211, 238').replace('#3b82f6', 'rgba(59, 130, 246').replace('#10b981', 'rgba(16, 185, 129').replace('#f59e0b', 'rgba(245, 158, 11').replace('#ec4899', 'rgba(236, 72, 153'));

          // Simplified color to rgba
          let rgba = 'rgba(168, 85, 247, 0.2)';
          if (node.color === '#22d3ee') rgba = 'rgba(34, 211, 238, 0.2)';
          else if (node.color === '#3b82f6') rgba = 'rgba(59, 130, 246, 0.2)';
          else if (node.color === '#10b981') rgba = 'rgba(16, 185, 129, 0.2)';
          else if (node.color === '#f59e0b') rgba = 'rgba(245, 158, 11, 0.2)';
          else if (node.color === '#ec4899') rgba = 'rgba(236, 72, 153, 0.2)';

          const glowGradient = ctx.createRadialGradient(
            node.x, node.y, 0,
            node.x, node.y, node.size * 2 * pulse
          );
          glowGradient.addColorStop(0, rgba);
          glowGradient.addColorStop(1, 'transparent');

          ctx.fillStyle = glowGradient;
          ctx.beginPath();
          ctx.arc(node.x, node.y, node.size * 2 * pulse, 0, Math.PI * 2);
          ctx.fill();

          // Avatar circle
          ctx.beginPath();
          ctx.arc(node.x, node.y, node.size, 0, Math.PI * 2);
          ctx.fillStyle = rgba.replace('0.2', '0.3');
          ctx.fill();

          // Avatar border
          ctx.strokeStyle = rgba.replace('0.2', '0.5');
          ctx.lineWidth = 2;
          ctx.stroke();

          // Initials
          ctx.font = 'bold 12px sans-serif';
          ctx.fillStyle = rgba.replace('0.2', '0.7');
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(node.initials || '', node.x, node.y);
        } else {
          // Regular node
          ctx.beginPath();
          ctx.arc(node.x, node.y, node.size * pulse, 0, Math.PI * 2);

          let nodeRgba = 'rgba(168, 85, 247, 0.15)';
          if (node.color === '#22d3ee') nodeRgba = 'rgba(34, 211, 238, 0.15)';
          else if (node.color === '#3b82f6') nodeRgba = 'rgba(59, 130, 246, 0.15)';

          ctx.fillStyle = nodeRgba;
          ctx.fill();
        }
      });
    };

    const drawHeart = (x: number, y: number, size: number, opacity: number) => {
      ctx.save();
      ctx.translate(x, y);

      ctx.beginPath();
      ctx.moveTo(0, size * 0.3);
      ctx.bezierCurveTo(-size, -size * 0.3, -size, -size, 0, -size * 0.5);
      ctx.bezierCurveTo(size, -size, size, -size * 0.3, 0, size * 0.3);

      ctx.fillStyle = `rgba(236, 72, 153, ${opacity})`;
      ctx.fill();

      ctx.restore();
    };

    const drawHearts = () => {
      hearts.forEach((heart) => {
        heart.y += heart.vy;
        heart.x += Math.sin(time + heart.phase) * 0.5;

        if (heart.y < -heart.size) {
          heart.y = canvas.height + heart.size;
          heart.x = Math.random() * canvas.width;
        }

        const pulse = Math.sin(time * 3 + heart.phase) * 0.1 + 1;
        drawHeart(heart.x, heart.y, heart.size * pulse, heart.opacity);
      });
    };

    const drawCommunityRings = () => {
      const centerX = canvas.width * 0.5;
      const centerY = canvas.height * 0.5;

      for (let i = 0; i < 3; i++) {
        const radius = 150 + i * 80;
        const opacity = 0.03 - i * 0.008;
        const rotation = time * 0.1 * (i % 2 === 0 ? 1 : -1);

        ctx.save();
        ctx.translate(centerX, centerY);
        ctx.rotate(rotation);

        ctx.beginPath();
        ctx.arc(0, 0, radius, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(168, 85, 247, ${opacity})`;
        ctx.lineWidth = 1;
        ctx.setLineDash([10, 20]);
        ctx.stroke();

        ctx.restore();
      }
    };

    const drawActivityPulse = () => {
      const x = canvas.width * 0.9;
      const y = canvas.height * 0.2;

      // Pulse rings
      for (let i = 0; i < 3; i++) {
        const age = (time * 0.5 + i * 0.3) % 1;
        const radius = age * 50;
        const opacity = (1 - age) * 0.1;

        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(16, 185, 129, ${opacity})`;
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      // Center dot
      ctx.beginPath();
      ctx.arc(x, y, 5, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(16, 185, 129, 0.3)';
      ctx.fill();
    };

    const animate = () => {
      time += 0.016;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Background gradient
      const gradient = ctx.createRadialGradient(
        canvas.width * 0.5,
        canvas.height * 0.5,
        0,
        canvas.width * 0.5,
        canvas.height * 0.5,
        canvas.width * 0.7
      );
      gradient.addColorStop(0, 'rgba(168, 85, 247, 0.02)');
      gradient.addColorStop(0.5, 'rgba(236, 72, 153, 0.01)');
      gradient.addColorStop(1, 'transparent');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      drawCommunityRings();
      drawConnections();
      drawNodes();
      drawHearts();
      drawActivityPulse();

      animationFrameId = requestAnimationFrame(animate);
    };

    resize();
    window.addEventListener('resize', resize);
    animate();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      <canvas
        ref={canvasRef}
        className="w-full h-full opacity-80"
      />

      {/* Decorative elements */}
      <div className="absolute top-28 left-8 hidden lg:block">
        <div className="flex items-center gap-1 animate-pulse">
          <div className="w-2 h-2 rounded-full bg-green-500/40" />
          <div className="w-2 h-2 rounded-full bg-green-500/30" />
          <div className="w-2 h-2 rounded-full bg-green-500/20" />
        </div>
      </div>

      <div className="absolute bottom-24 left-12 hidden lg:block">
        <div className="text-xs text-purple-400/20 font-mono">
          <div>+1 joined</div>
          <div className="mt-1">+3 online</div>
        </div>
      </div>

      <div className="absolute top-1/3 right-6 hidden lg:block">
        <div className="w-12 h-12 rounded-full border border-purple-500/10 flex items-center justify-center animate-pulse">
          <div className="w-8 h-8 rounded-full border border-purple-500/20 flex items-center justify-center">
            <div className="w-4 h-4 rounded-full bg-purple-500/10" />
          </div>
        </div>
      </div>
    </div>
  );
}
